import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ProductGalleryProps {
  productName: string;
  image: string;
  galleryImages?: string[];
}

export default function ProductGallery({ productName, image, galleryImages = [] }: ProductGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const finalImages = [image, ...galleryImages.filter((img) => img && img !== image)].filter(Boolean);
  const activeImage = finalImages[activeIndex] || image;
  const hasActiveImage = Boolean(activeImage && !imageError);
  const canNavigate = finalImages.length > 1;

  const showPrevious = useCallback(() => {
    if (!canNavigate) return;
    setActiveIndex((current) => (current - 1 + finalImages.length) % finalImages.length);
  }, [canNavigate, finalImages.length]);

  const showNext = useCallback(() => {
    if (!canNavigate) return;
    setActiveIndex((current) => (current + 1) % finalImages.length);
  }, [canNavigate, finalImages.length]);

  useEffect(() => {
    setImageError(false);
  }, [activeImage]);

  useEffect(() => {
    if (!lightboxOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setLightboxOpen(false);
      if (event.key === 'ArrowLeft') showPrevious();
      if (event.key === 'ArrowRight') showNext();
    }

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, showNext, showPrevious]);

  const lightbox = lightboxOpen ? (
    <div className="fixed inset-0 z-[9999] bg-[#07130e]/95 p-3 md:p-6" role="dialog" aria-modal="true" aria-label="產品圖片放大檢視">
      <div className="mx-auto flex h-full max-w-7xl flex-col">
        <div className="mb-3 flex items-center justify-between gap-3 text-white">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{productName}</div>
            <div className="text-xs text-white/70">{activeIndex + 1} / {finalImages.length}</div>
          </div>
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/12 text-xl text-white hover:bg-white/20"
            onClick={() => setLightboxOpen(false)}
            aria-label="關閉圖片放大檢視"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="relative flex min-h-0 flex-1 items-center justify-center rounded-xl bg-black/20">
          {canNavigate && (
            <button
              type="button"
              className="absolute left-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/14 text-2xl text-white backdrop-blur hover:bg-white/24 md:left-4"
              onClick={showPrevious}
              aria-label="上一張圖片"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          )}
          <img
            src={activeImage}
            alt={`${productName} 放大圖片 ${activeIndex + 1}`}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
          {canNavigate && (
            <button
              type="button"
              className="absolute right-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/14 text-2xl text-white backdrop-blur hover:bg-white/24 md:right-4"
              onClick={showNext}
              aria-label="下一張圖片"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          )}
        </div>

        {finalImages.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {finalImages.map((img, idx) => (
              <button
                type="button"
                key={img}
                onClick={() => setActiveIndex(idx)}
                className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-white/10 md:h-20 md:w-20 ${
                  idx === activeIndex ? 'border-white' : 'border-white/20 hover:border-white/60'
                }`}
                aria-label={`切換到第 ${idx + 1} 張圖片`}
              >
                <img src={img} alt={`${productName} 放大縮圖 ${idx + 1}`} className="h-full w-full object-cover object-top" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div>
      {/* 主圖 */}
      <button
        type="button"
        className="group relative h-72 w-full overflow-hidden rounded-2xl bg-[#FAF8F5] text-left md:h-[500px]"
        onClick={() => activeImage && setLightboxOpen(true)}
        aria-label="放大查看產品圖片"
      >
        {hasActiveImage ? (
          <img
            src={activeImage}
            alt={`${productName} - ${activeIndex === 0 ? '正面' : activeIndex === 1 ? '側面' : '細節'}`}
            onError={() => setImageError(true)}
            className="relative z-10 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-[#9A8A7A]">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
              <i className="ri-image-line text-xl text-[#B9A58F]" />
            </div>
            <span className="text-sm font-semibold">圖片準備中</span>
          </div>
        )}
        {/* 圖片放大提示 */}
        <div className="absolute bottom-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 shadow-sm backdrop-blur-sm transition-transform duration-300 group-hover:scale-105">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-zoom-in-line text-[#1B3D2A]" />
          </div>
        </div>
        {/* 圖片計數器 */}
        <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs text-[#5A6B5A] font-medium">
          {activeIndex + 1} / {Math.max(finalImages.length, 1)}
        </div>
      </button>

      {/* 縮圖 */}
      {finalImages.length > 1 && <div className="mt-4 flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
        {finalImages.map((img, idx) => (
          <button
            type="button"
            key={idx}
            onClick={() => setActiveIndex(idx)}
            className={`w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all duration-300 snap-start ${
              idx === activeIndex
                ? 'border-[#1B3D2A] ring-2 ring-[#1B3D2A]/10'
                : 'border-[#E8E0D6] hover:border-[#D4A574]'
            }`}
          >
            <img
              src={img}
              alt={`${productName} 縮圖 ${idx + 1}`}
              onError={(event) => {
                event.currentTarget.style.opacity = '0';
              }}
              className="w-full h-full object-cover object-top"
            />
          </button>
        ))}
      </div>}

      {lightbox && createPortal(lightbox, document.body)}
    </div>
  );
}
